import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1646191152321-673e8b541cb8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800&auto=format";

const PILLARS = [
  {
    n: "01",
    title: "Matière",
    text: "French terry 500 GSM, ripstop japonais, toile cirée. Des tissus choisis comme s’ils comptaient — parce qu’ils comptent.",
  },
  {
    n: "02",
    title: "Main",
    text: "Coupé et fini dans notre atelier parisien. Points d’arrêt, quincaillerie posée à la main, zéro raccourci.",
  },
  {
    n: "03",
    title: "Mémoire",
    text: "Chaque pièce porte le nom de ce qu’elle a survécu. Le vêtement comme témoignage, jamais comme décoration.",
  },
];

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div data-testid="about-page">
      <div className="mx-auto max-w-[1700px] px-5 pt-36 sm:px-8 lg:px-16 lg:pt-44">
        <Reveal>
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-ember">La Maison — Fondée en MMXXI</p>
          <h1 className="mt-4 font-blackletter text-6xl leading-[1] sm:text-8xl lg:text-9xl">
            Nés de la Lutte
          </h1>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12">
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="font-serif text-2xl italic leading-snug text-zinc-300 sm:text-3xl">
              « malgré les difficultés » — pas un slogan. Une condition de naissance.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="lg:col-span-6 lg:col-start-7">
            <div className="space-y-6 text-sm leading-relaxed text-zinc-400 sm:text-base">
              <p>
                La maison est née dans un sous-sol du 10e arrondissement : deux machines à coudre, un
                projecteur industriel, des rouleaux de tissu achetés avec l’argent du loyer. Pas
                d’investisseurs, pas d’héritage — seulement le travail.
              </p>
              <p>
                Le designer a grandi sans rien — vêtements de seconde main et poches vides dans la
                banlieue parisienne. Mais un rêve refusait de se taire : créer un art que l’on porte,
                et la volonté de vendre cet art au monde entier. Chaque croquis était une porte de
                sortie ; chaque couture, un pas de plus.
              </p>
              <p>
                C’est pourquoi aucune pièce n’est jamais qu’un simple tissu. Chaque vêtement porte un
                chapitre de l’ascension — le doute, la faim, les nuits interminables. Porter malgré
                les difficultés, c’est porter une histoire cousue d’émotion : la preuve que la lutte
                peut devenir quelque chose de beau.
              </p>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-24 lg:mt-32">
        <Marquee />
      </div>

      <div className="mx-auto max-w-[1700px] px-5 py-24 sm:px-8 lg:px-16 lg:py-36">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.12}>
              <div className="border-t border-white/10 pt-8">
                <span className="font-mono text-xs tracking-[0.3em] text-ember">{p.n}</span>
                <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight">{p.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-zinc-400">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div ref={imgRef} className="relative h-[55vh] overflow-hidden lg:h-[80vh]">
        <motion.img
          src={ABOUT_IMG}
          alt="Atelier portrait — dramatic studio light"
          style={{ y }}
          loading="lazy"
          className="h-[120%] w-full object-cover"
        />
        <div className="absolute inset-0 flex items-end bg-black/35">
          <p className="max-w-3xl p-7 font-serif text-2xl italic leading-snug text-zinc-100 sm:p-14 sm:text-4xl">
            « Nous n’avons pas échappé aux difficultés. Nous les avons brodées. »
          </p>
        </div>
      </div>
    </div>
  );
}
